import { followSharedTooltipCursor } from "../../../utils/chart-tooltip";

export const timeseriesChartConfig: any = {
  series: [
    {
      name: "Daily Precipitation",
      data: [],
      color: "#06b6d4",
      show: false,
    },
    {
      name: "Daily ET",
      data: [],
      color: "#1A56DB",
    },
    {
      name: "Cumulative ET",
      data: [],
      color: "#7E3BF2",
      show: false,
    },
  ],
  chart: {
    events: { mouseMove: followSharedTooltipCursor },
    sparkline: {
      enabled: false,
    },
    height: "100%",
    grid: {
      padding: {
        bottom: 5,
        left: 50,
      },
    },
    type: "area",
    fontFamily: "Inter, sans-serif",
    dropShadow: {
      enabled: false,
    },
    toolbar: {
      show: true,
      offsetX: -20,
      tools: {
        reset: true,
        zoom: true,
        selection: false,
        download: false,
        zoomin: false,
        zoomout: false,
        pan: false,
      },
    },
    zoom: {
      autoScaleYaxis: true,
    },
  },
  xaxis: {
    crosshairs: { show: false },
    show: true,
    categories: [],
    labels: {
      show: true,
      style: {
        fontFamily: "Inter, sans-serif",
        cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
      },
      tickAmount: 10,
      rotate: -30,
      rotateAlways: false,
      hideOverlappingLabels: true,
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: [
    {
      seriesName: "Daily Precipitation",
      opposite: false,
      title: {
        text: "Precipitation (Inches)",
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
        },
      },
      min: 0,
      show: true,
      labels: {
        show: true,
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
        },
        formatter: function (value: number) {
          return value;
        },
      },
    },
    {
      seriesName: "Daily ET",
      opposite: true,
      min: 0,
      title: {
        text: "Evapotranspiration (Inches)",
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
        },
      },
      show: true,
      labels: {
        show: true,
        style: {
          fontFamily: "Inter, sans-serif",
          cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
        },
        formatter: function (value: number) {
          return value;
        },
      },
    },
  ],
  tooltip: {
    enabled: true,
    shared: true,
    intersect: false,
    followCursor: true,
    fixed: { enabled: false },
    x: {
      show: false,
    },
  },
  fill: {
    type: "gradient",
    gradient: {
      opacityFrom: 0.55,
      opacityTo: 0.1,
    },
  },
  dataLabels: {
    enabled: false,
  },
  legend: {
    show: true,
    position: "top",
    labels: {
      colors: "#64748b",
    },
  },
  grid: {
    show: true,
  },
};

export const getUpdateTimeseriesChartConfig = (
  precipitation: (number | null)[],
  cprcp: (number | null)[],
  etas: (number | null)[],
  cetas: (number | null)[],
  dateList: string[],
): any => {
  return {
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: true,
      fixed: { enabled: false },
    },
    grid: {
      padding: {
        left: 50,
        right: 40,
      },
    },
    series: [
      {
        name: "Average Daily Precipitation",
        data: precipitation,
        color: "#E74694",
        type: "column",
      },
      {
        name: "Cumulative Precipitation",
        data: cprcp,
        color: "#7E3AF2",
        type: "area",
        hidden: true,
      },
      {
        name: "Average Daily ET",
        data: etas,
        color: "#0E9F6E",
        type: "area",
      },
      {
        name: "Cumulative ET",
        data: cetas,
        color: "#C27803",
        type: "area",
        hidden: true,
      },
    ],
    stroke: {
      width: [2, 5, 5, 3],
      curve: "smooth",
      dashArray: [0, 0, 0, 5],
    },
    xaxis: {
      crosshairs: { show: false },
      categories: dateList,
      tickAmount: 12,
      formatter: function (value: number) {
        return value;
      },
    },
    yaxis: [
      {
        seriesName: "Average Daily Precipitation",
        opposite: false,
        title: {
          text: "Precipitation (Inches)",
          style: {
            cssClass: "text-sm font-normal fill-gray-500 dark:fill-gray-400",
          },
        },
        show: true,
        labels: {
          show: true,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
          formatter: function (value: number) {
            // ApexCharts may pass undefined or non-numeric values on some calls
            if (typeof value === "number" && value > 0.02) {
              // Format numeric values to 1 decimal place
              return value.toFixed(2);
            }
            // Otherwise, return something safe, like an empty string or the raw value
            return 0;
          },
        },
      },
      {
        seriesName: "Average Daily Precipitation",
        opposite: true,
        min: 0,
        title: {
          text: "Precipitation (Inches)",
          style: {
            cssClass: "text-sm font-medium fill-gray-500 dark:fill-gray-400",
          },
        },
        show: true,
        labels: {
          show: true,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
          formatter: function (value: number) {
            // ApexCharts may pass undefined or non-numeric values on some calls
            if (typeof value === "number") {
              // Format numeric values to 1 decimal place
              return value.toFixed(2);
            }
            // Otherwise, return something safe, like an empty string or the raw value
            return "";
          },
        },
      },
      {
        seriesName: "Average Daily ET",
        opposite: true,
        min: 0,
        title: {
          text: "Evapotranspiration (Inches)",
          style: {
            cssClass: "text-sm font-medium fill-gray-500 dark:fill-gray-400",
          },
        },
        show: true,
        labels: {
          show: true,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
          formatter: function (value: number) {
            // ApexCharts may pass undefined or non-numeric values on some calls
            if (typeof value === "number") {
              // Format numeric values to 1 decimal place
              return value.toFixed(2);
            }
            // Otherwise, return something safe, like an empty string or the raw value
            return "";
          },
        },
      },
      {
        seriesName: "Average Daily ET",
        opposite: true,
        min: 0,
        title: {
          text: "Evapotranspiration (Inches)",
          style: {
            cssClass: "text-sm font-medium fill-gray-500 dark:fill-gray-400",
          },
        },
        show: true,
        labels: {
          show: true,
          style: {
            fontFamily: "Inter, sans-serif",
            cssClass: "text-xs font-normal fill-gray-500 dark:fill-gray-400",
          },
          formatter: function (value: number) {
            // ApexCharts may pass undefined or non-numeric values on some calls
            if (typeof value === "number") {
              // Format numeric values to 1 decimal place
              return value.toFixed(2);
            }
            // Otherwise, return something safe, like an empty string or the raw value
            return "";
          },
        },
      },
    ],
    legend: {
      show: true,
      position: "top",
      labels: {
        colors: "#64748b",
      },
      clusterGroupedSeries: false,
    },
    plotOptions: {
      bar: {
        columnWidth: "50%",
      },
    },
  };
};
